import { useState, useEffect, useCallback } from 'react';
import { X, DeviceMobile, Desktop, ArrowSquareOut, PlusSquare, ShareNetwork, DotsThreeVertical } from '@phosphor-icons/react';

function getPlatform() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid || window.innerWidth < 768;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  return { isIOS, isAndroid, isMobile, isStandalone };
}

export default function InstallBanner() {
  const [show, setShow] = useState(false);
  const [closing, setClosing] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform] = useState(getPlatform);

  useEffect(() => {
    if (platform.isStandalone) return;
    if (localStorage.getItem('pwa_install_dismissed')) return;

    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    const timer = setTimeout(() => setShow(true), 4000);
    return () => { window.removeEventListener('beforeinstallprompt', handler); clearTimeout(timer); };
  }, [platform.isStandalone]);

  const dismiss = useCallback(() => {
    setClosing(true);
    setTimeout(() => { setShow(false); setClosing(false); localStorage.setItem('pwa_install_dismissed', Date.now().toString()); }, 350);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') dismiss();
      setDeferredPrompt(null);
    }
  };

  if (!show || platform.isStandalone) return null;

  const canNativeInstall = deferredPrompt && !platform.isIOS;

  /* ── MOBILE ── */
  if (platform.isMobile) {
    return (
      <>
        <div
          className={`fixed inset-0 z-[998] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${closing ? 'opacity-0' : 'opacity-100'}`}
          onClick={dismiss}
        />
        <div
          className={`fixed z-[999] bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl transition-transform duration-350 ${closing ? 'translate-y-full' : ''}`}
          style={{ animation: closing ? 'none' : 'ib-up .4s cubic-bezier(.16,1,.3,1)' }}
          data-testid="pwa-install-banner"
        >
          <div className="flex justify-center pt-2.5 pb-1"><div className="w-10 h-1 bg-zinc-200 rounded-full" /></div>
          <button onClick={dismiss} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-zinc-100" data-testid="pwa-install-dismiss">
            <X size={16} className="text-zinc-400" />
          </button>

          <div className="px-5 pt-3 pb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 bg-zinc-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <DeviceMobile size={22} weight="bold" className="text-white" />
              </div>
              <div>
                <p className="text-[15px] font-black text-zinc-900 leading-tight">Aggiungi alla Home</p>
                <p className="text-xs text-zinc-400">Accesso rapido, come un'app</p>
              </div>
            </div>

            {canNativeInstall ? (
              <button
                onClick={handleInstall}
                className="w-full py-3 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider rounded-xl active:scale-[.98] transition-transform"
                data-testid="pwa-install-button"
              >
                Installa
              </button>
            ) : (
              <div className="space-y-2">
                {platform.isIOS ? (
                  <>
                    <Step n={1} icon={<ShareNetwork size={16} weight="bold" className="text-blue-500" />} text={<>Tocca <b>Condividi</b> in basso</>} />
                    <Step n={2} icon={<PlusSquare size={16} weight="bold" className="text-zinc-700" />} text={<>Seleziona <b>Aggiungi a Home</b></>} />
                  </>
                ) : (
                  <>
                    <Step n={1} icon={<DotsThreeVertical size={16} weight="bold" className="text-zinc-700" />} text={<>Apri il <b>menu del browser</b></>} />
                    <Step n={2} icon={<PlusSquare size={16} weight="bold" className="text-zinc-700" />} text={<><b>Installa app</b> o <b>Aggiungi a Home</b></>} />
                  </>
                )}
              </div>
            )}

            <button onClick={dismiss} className="w-full mt-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-600" data-testid="pwa-install-later">
              Magari dopo
            </button>
          </div>
        </div>
        <style>{`@keyframes ib-up{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
      </>
    );
  }

  /* ── DESKTOP ── */
  return (
    <>
      <div
        className={`fixed z-[999] bottom-5 right-5 w-[360px] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-350 ${closing ? 'translate-y-6 opacity-0' : ''}`}
        style={{ animation: closing ? 'none' : 'ib-pop .45s cubic-bezier(.16,1,.3,1)' }}
        data-testid="pwa-install-banner"
      >
        <div className="h-1 bg-zinc-900" />
        <button onClick={dismiss} className="absolute top-2.5 right-2.5 p-1.5 rounded-full hover:bg-zinc-100 z-10" data-testid="pwa-install-dismiss">
          <X size={16} className="text-zinc-400" />
        </button>

        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-zinc-900 rounded-xl flex items-center justify-center flex-shrink-0">
              <Desktop size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <p className="text-sm font-black text-zinc-900">Installa l'app</p>
              <p className="text-xs text-zinc-400">Accesso rapido dal desktop</p>
            </div>
          </div>

          {canNativeInstall ? (
            <button
              onClick={handleInstall}
              className="w-full py-2.5 bg-zinc-900 text-white text-xs font-bold uppercase tracking-wider rounded-xl active:scale-[.98] transition-transform"
              data-testid="pwa-install-button"
            >
              Installa
            </button>
          ) : (
            <div className="bg-zinc-50 rounded-xl p-2.5 text-center">
              <p className="text-xs text-zinc-500">
                Cerca <ArrowSquareOut size={13} weight="bold" className="inline text-zinc-600" /> nella barra del browser
              </p>
            </div>
          )}

          <button onClick={dismiss} className="w-full mt-2.5 py-1 text-[11px] text-zinc-400 hover:text-zinc-600" data-testid="pwa-install-later">
            Magari dopo
          </button>
        </div>
      </div>
      <style>{`@keyframes ib-pop{from{transform:translateY(30px) scale(.96);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}`}</style>
    </>
  );
}

function Step({ n, icon, text }) {
  return (
    <div className="flex items-center gap-2.5 bg-zinc-50 rounded-xl px-3 py-2.5">
      <div className="w-5 h-5 bg-zinc-900 rounded-full flex items-center justify-center flex-shrink-0">
        <span className="text-[10px] font-black text-white">{n}</span>
      </div>
      {icon}
      <p className="text-[13px] text-zinc-700">{text}</p>
    </div>
  );
}
